import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, DatePicker, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ScheduleChangeLogListQueryDTO } from "../../domain/contracts/schedule-logs";

const sessionTypeOptions = [
  { value: "all", label: "ທຸກປະເພດ" },
  { value: "class_session", label: "ຄລາດສ໌" },
  { value: "private_lesson_booking", label: "ວິຊາສ່ວນຕົວ" },
  { value: "room_booking", label: "ຈອງຫ້ອງ" },
];

const changeTypeOptions = [
  { value: "all", label: "ທຸກປະເພດ" },
  { value: "time", label: "ປັບເວລາ" },
  { value: "room", label: "ປ່ຽນຫ້ອງ" },
  { value: "instructor", label: "ປ່ຽນຄູຜູ້ສອນ" },
  { value: "capacity", label: "ປ່ຽນຄວາມຈຸ" },
  { value: "other", label: "ອື່ນໆ" },
];

export function ScheduleChangeLogFilter() {
  const nav = useNavigate({ from: "/app/classes/schedule-logs" });
  const search = useSearch({
    from: "/app/classes/schedule-logs",
  }) as Partial<ScheduleChangeLogListQueryDTO> & {
    limit?: number;
    offset?: number;
  };

  const sessionType = search.sessionType ?? "all";
  const changeType = search.changeType ?? "all";
  const dateFromStr = search.dateFrom ?? "";
  const dateToStr = search.dateTo ?? "";
  const reasonFromUrl = search.reason ?? "";

  const [fromDate, setFromDate] = useState<Date | undefined>(
    dateFromStr ? new Date(dateFromStr) : undefined,
  );
  const [toDate, setToDate] = useState<Date | undefined>(
    dateToStr ? new Date(dateToStr) : undefined,
  );
  const [searchValue, setSearchValue] = useState(reasonFromUrl);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSearchValue(reasonFromUrl);
  }, [reasonFromUrl]);

  useEffect(() => {
    setFromDate(dateFromStr ? new Date(dateFromStr) : undefined);
  }, [dateFromStr]);
  useEffect(() => {
    setToDate(dateToStr ? new Date(dateToStr) : undefined);
  }, [dateToStr]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    nav({
      search: {
        ...search,
        offset: 0,
        reason: val.trim() || undefined,
      },
    });
  }, 400);

  const hasFilter =
    sessionType !== "all" ||
    changeType !== "all" ||
    !!dateFromStr ||
    !!dateToStr ||
    !!reasonFromUrl;

  const handleClear = () => {
    if (inputRef.current) inputRef.current.value = "";
    setSearchValue("");
    setFromDate(undefined);
    setToDate(undefined);
    nav({
      search: {
        limit: search.limit ?? 20,
        offset: 0,
      },
    });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາເຫດຜົນ..."
            defaultValue={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-2xs"
            ref={inputRef}
          />

          <SimpleSelect
            value={sessionType}
            onValueChange={(val) => {
              nav({
                search: {
                  ...search,
                  offset: 0,
                  sessionType: val === "all" ? undefined : val,
                },
              });
            }}
            placeholder="ປະເພດ"
            className="h-8 w-full sm:w-36"
            options={sessionTypeOptions}
          />

          <SimpleSelect
            value={changeType}
            onValueChange={(val) => {
              nav({
                search: {
                  ...search,
                  offset: 0,
                  changeType: val === "all" ? undefined : val,
                },
              });
            }}
            placeholder="ປະເພດການປ່ຽນແປງ"
            className="h-8 w-full sm:w-36"
            options={changeTypeOptions}
          />

          <DatePicker
            mode="range"
            value={{
              from: fromDate,
              to: toDate,
            }}
            onChange={(range) => {
              const from: Date | undefined = Array.isArray(range)
                ? range[0]
                : range?.from;
              const to: Date | undefined = Array.isArray(range)
                ? range[1]
                : range?.to;
              setFromDate(from);
              setToDate(to);
              const fromStr = from ? format(from, "yyyy-MM-dd") : "";
              const toStr = to ? format(to, "yyyy-MM-dd") : "";
              nav({
                search: {
                  ...search,
                  offset: 0,
                  dateFrom: fromStr || undefined,
                  dateTo: toStr || undefined,
                },
              });
            }}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />
        </div>

        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="max-sm:w-full"
          >
            <XIcon className="size-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
